import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { X, Plus, UserPlus, Save, Palette } from "lucide-react";

const commonSpecializations = [
  "General Practice",
  "Internal Medicine",
  "Cardiology",
  "Dermatology",
  "Orthopedics",
  "Pediatrics",
  "Psychiatry",
  "Radiology",
  "Surgery",
  "Neurology",
  "Gynecology",
  "Oncology",
  "Endocrinology",
  "Gastroenterology",
  "Pulmonology",
  "Urology",
  "Ophthalmology",
  "ENT (Otolaryngology)",
  "Anesthesiology",
  "Emergency Medicine",
];

export default function AddDoctorModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    specializations: [],
    isOwner: false,
    status: "invited", // invited | active
    color: "#3b82f6", // Default blue color
  });
  const [customSpecialization, setCustomSpecialization] = useState("");
  const [errors, setErrors] = useState({});

  const queryClient = useQueryClient();

  const createDoctorMutation = useMutation({
    mutationFn: (doctorData) =>
      apiClient.post("/api/users/doctors", doctorData),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["admin-doctors"]); // Invalidate admin doctors list
      queryClient.invalidateQueries(["doctors"]); // Also invalidate booking doctors list
      onSuccess?.(data);
      onClose();
    },
    onError: (error) => {
      setErrors({
        submit: error.data.error || "Failed to create doctor",
      });
    },
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const addSpecialization = (specialization) => {
    if (specialization && !formData.specializations.includes(specialization)) {
      setFormData((prev) => ({
        ...prev,
        specializations: [...prev.specializations, specialization],
      }));
    }
  };

  const removeSpecialization = (specialization) => {
    setFormData((prev) => ({
      ...prev,
      specializations: prev.specializations.filter((s) => s !== specialization),
    }));
  };

  const addCustomSpecialization = () => {
    if (customSpecialization.trim()) {
      addSpecialization(customSpecialization.trim());
      setCustomSpecialization("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (formData.specializations.length === 0) {
      newErrors.specializations = "At least one specialization is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    createDoctorMutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Add Doctor to Practice
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Doctor Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    placeholder="John"
                    className={errors.firstName ? "border-red-500" : ""}
                    data-testid="doctor-firstname-input"
                  />
                  {errors.firstName && (
                    <p className="text-sm text-red-600">{errors.firstName}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                    placeholder="Smith"
                    className={errors.lastName ? "border-red-500" : ""}
                    data-testid="doctor-lastname-input"
                  />
                  {errors.lastName && (
                    <p className="text-sm text-red-600">{errors.lastName}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="john.smith@example.com"
                  className={errors.email ? "border-red-500" : ""}
                  data-testid="doctor-email-input"
                />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="color">Display Color</Label>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-10 rounded-md border-2 border-slate-300 cursor-pointer flex items-center justify-center"
                    style={{ backgroundColor: formData.color }}
                    onClick={() => document.getElementById('colorPicker').click()}
                  >
                    <Palette className="w-4 h-4 text-white opacity-75" />
                  </div>
                  <input
                    id="colorPicker"
                    type="color"
                    value={formData.color}
                    onChange={(e) => handleInputChange("color", e.target.value)}
                    className="hidden"
                  />
                  <div className="flex-1">
                    <Input
                      value={formData.color}
                      onChange={(e) => handleInputChange("color", e.target.value)}
                      placeholder="#3b82f6"
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-slate-600 mt-1">
                      Color used for calendar and UI elements
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Specializations */}
            <div className="space-y-4">
              <div>
                <Label>Specializations *</Label>
                <p className="text-sm text-slate-600 mt-1">
                  Select or add medical specializations for this doctor
                </p>
              </div>

              {/* Selected Specializations */}
              {formData.specializations.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.specializations.map((specialization) => (
                    <Badge
                      key={specialization}
                      variant="secondary"
                      className="px-3 py-1"
                    >
                      {specialization}
                      <button
                        type="button"
                        onClick={() => removeSpecialization(specialization)}
                        className="ml-2 text-slate-500 hover:text-slate-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Add from preset list */}
              <div className="space-y-2">
                <Label>Add from common specializations:</Label>
                <Select onValueChange={addSpecialization}>
                  <SelectTrigger data-testid="doctor-specialization-select">
                    <SelectValue placeholder="Select a specialization" />
                  </SelectTrigger>
                  <SelectContent>
                    {commonSpecializations
                      .filter((s) => !formData.specializations.includes(s))
                      .map((specialization) => (
                        <SelectItem key={specialization} value={specialization} data-testid={`doctor-specialization-option-${specialization.toLowerCase().replace(/\s+/g, '-')}`}>
                          {specialization}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Add custom specialization */}
              <div className="flex gap-2">
                <Input
                  value={customSpecialization}
                  onChange={(e) => setCustomSpecialization(e.target.value)}
                  placeholder="Or enter a custom specialization"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomSpecialization();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addCustomSpecialization}
                  disabled={!customSpecialization.trim()}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {errors.specializations && (
                <p className="text-sm text-red-600">{errors.specializations}</p>
              )}
            </div>

            {/* Doctor Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label htmlFor="isOwner" className="font-medium">
                      Practice Owner
                    </Label>
                    <p className="text-sm text-slate-600">
                      Grant this doctor practice owner privileges
                    </p>
                  </div>
                  <Switch
                    id="isOwner"
                    checked={formData.isOwner}
                    onCheckedChange={(checked) =>
                      handleInputChange("isOwner", checked)
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Initial Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) =>
                      handleInputChange("status", value)
                    }
                  >
                    <SelectTrigger data-testid="doctor-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invited" data-testid="doctor-status-invited">
                        Invited (requires email verification)
                      </SelectItem>
                      <SelectItem value="active" data-testid="doctor-status-active">
                        Active (immediately accessible)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={createDoctorMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createDoctorMutation.isPending}
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
                data-testid="doctor-submit-btn"
              >
                {createDoctorMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create Doctor
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
